import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Users,
  Percent,
  Calendar,
  RefreshCw,
  Clock,
  Printer,
  ShoppingBag,
  Briefcase,
  Shirt,
  Truck,
  FileText,
  Target,
  Award,
  Layers,
  Info,
} from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import MainNav from "@/components/MainNav";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Time period presets
const TIME_PERIODS = {
  this_week: { label: "This Week", getRange: () => getThisWeekRange() },
  last_week: { label: "Last Week", getRange: () => getLastWeekRange() },
  this_month: { label: "This Month", getRange: () => getThisMonthRange() },
  last_month: { label: "Last Month", getRange: () => getLastMonthRange() },
  this_quarter: { label: "This Quarter (FY)", getRange: () => getThisQuarterRange() },
  last_quarter: { label: "Last Quarter (FY)", getRange: () => getLastQuarterRange() },
  this_fy: { label: "This Financial Year", getRange: () => getThisFYRange() },
  last_fy: { label: "Last Financial Year", getRange: () => getLastFYRange() },
  custom: { label: "Custom Range", getRange: null },
};

// Helper functions for date ranges (FY starts July 1)
function getThisWeekRange() {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - now.getDay() + 1);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: formatDate(monday), end: formatDate(sunday) };
}

function getLastWeekRange() {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - now.getDay() - 6);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: formatDate(monday), end: formatDate(sunday) };
}

function getThisMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: formatDate(start), end: formatDate(end) };
}

function getLastMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { start: formatDate(start), end: formatDate(end) };
}

function getThisQuarterRange() {
  // Australian FY quarters: Jul-Sep, Oct-Dec, Jan-Mar, Apr-Jun
  const now = new Date();
  const month = now.getMonth();
  let qStart, qEnd;
  
  if (month >= 6 && month <= 8) { // Q1: Jul-Sep
    qStart = new Date(now.getFullYear(), 6, 1);
    qEnd = new Date(now.getFullYear(), 8, 30);
  } else if (month >= 9 && month <= 11) { // Q2: Oct-Dec
    qStart = new Date(now.getFullYear(), 9, 1);
    qEnd = new Date(now.getFullYear(), 11, 31);
  } else if (month >= 0 && month <= 2) { // Q3: Jan-Mar
    qStart = new Date(now.getFullYear(), 0, 1);
    qEnd = new Date(now.getFullYear(), 2, 31);
  } else { // Q4: Apr-Jun
    qStart = new Date(now.getFullYear(), 3, 1);
    qEnd = new Date(now.getFullYear(), 5, 30);
  }
  
  return { start: formatDate(qStart), end: formatDate(qEnd) };
}

function getLastQuarterRange() {
  const now = new Date();
  const month = now.getMonth();
  let qStart, qEnd;
  
  if (month >= 6 && month <= 8) { // Currently Q1, last is Q4 (Apr-Jun)
    qStart = new Date(now.getFullYear(), 3, 1);
    qEnd = new Date(now.getFullYear(), 5, 30);
  } else if (month >= 9 && month <= 11) { // Currently Q2, last is Q1 (Jul-Sep)
    qStart = new Date(now.getFullYear(), 6, 1);
    qEnd = new Date(now.getFullYear(), 8, 30);
  } else if (month >= 0 && month <= 2) { // Currently Q3, last is Q2 (Oct-Dec)
    qStart = new Date(now.getFullYear() - 1, 9, 1);
    qEnd = new Date(now.getFullYear() - 1, 11, 31);
  } else { // Currently Q4, last is Q3 (Jan-Mar)
    qStart = new Date(now.getFullYear(), 0, 1);
    qEnd = new Date(now.getFullYear(), 2, 31);
  }
  
  return { start: formatDate(qStart), end: formatDate(qEnd) };
}

function getThisFYRange() {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const start = new Date(year, 6, 1); // July 1
  const end = new Date(year + 1, 5, 30); // June 30
  return { start: formatDate(start), end: formatDate(end) };
}

function getLastFYRange() {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() - 1 : now.getFullYear() - 2;
  const start = new Date(year, 6, 1);
  const end = new Date(year + 1, 5, 30);
  return { start: formatDate(start), end: formatDate(end) };
}

function formatDate(date) {
  return date.toISOString().split("T")[0];
}

// Chart colors
const COLORS = ["#3b82f6", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899"];

// Metric Card Component with trend support
function MetricCard({ title, value, subtitle, icon: Icon, trend, color = "primary", description }) {
  const colorClasses = {
    primary: "bg-aa-primary/10 text-aa-primary",
    green: "bg-aa-success/10 text-aa-success",
    amber: "bg-aa-warning/10 text-aa-warning",
    purple: "bg-purple-100 text-purple-600",
    rose: "bg-rose-100 text-rose-600",
    orange: "bg-aa-retail/10 text-aa-retail",
  };
  
  const hasTrend = trend !== null && trend !== undefined && !isNaN(trend);
  
  return (
    <div className="bg-aa-card rounded-aa-lg p-6 shadow-aa-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1">
            <p className="text-aa-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
            {description && (
              <TooltipProvider delayDuration={100}>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-aa-primary transition-colors cursor-help"
                      data-testid={`stat-info-${title.toLowerCase().replace(/\s+/g, '-')}`}
                      aria-label={`About ${title}`}
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[240px] text-xs">
                    {description}
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            )}
          </div>
          <p className="text-aa-xl font-bold mt-1 text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-aa-xs text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-aa-md ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {hasTrend && (
        <div className="mt-3 flex items-center gap-1">
          <TrendingUp className={`w-4 h-4 ${trend >= 0 ? 'text-aa-success' : 'text-aa-error rotate-180'}`} />
          <span className={`text-aa-sm font-medium ${trend >= 0 ? 'text-aa-success' : 'text-aa-error'}`}>
            {trend >= 0 ? '+' : ''}{trend.toFixed(1)}%
          </span>
          <span className="text-aa-xs text-gray-400 ml-1">vs prev period</span>
        </div>
      )}
    </div>
  );
}

// Reusable info tooltip for chart / section titles
function InfoTip({ text, testid }) {
  return (
    <TooltipProvider delayDuration={100}>
      <UITooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-gray-400 hover:text-aa-primary transition-colors cursor-help inline-flex align-middle"
            aria-label="More info"
            data-testid={testid}
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px] text-xs">{text}</TooltipContent>
      </UITooltip>
    </TooltipProvider>
  );
}


export default function Analytics() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [activeTab, setActiveTab] = useState("overview");
  const [timePeriod, setTimePeriod] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [prevSummary, setPrevSummary] = useState(null);
  const [jobsOverTime, setJobsOverTime] = useState([]);
  const [productDemand, setProductDemand] = useState(null);
  const [decoration, setDecoration] = useState(null);
  const [profitability, setProfitability] = useState(null);
  // Phase 3 data
  const [quoteConversion, setQuoteConversion] = useState(null);
  const [labourProfitability, setLabourProfitability] = useState(null);
  const [bestSellers, setBestSellers] = useState(null);
  const [supplierAnalytics, setSupplierAnalytics] = useState(null);
  const [transferAnalytics, setTransferAnalytics] = useState(null);
  
  // Get token from localStorage
  const getToken = () => localStorage.getItem("auth_token");
  
  // Calculate date range based on selected period
  const dateRange = useMemo(() => {
    if (timePeriod === "custom") {
      return { start: customStart, end: customEnd };
    }
    return TIME_PERIODS[timePeriod]?.getRange() || getThisMonthRange();
  }, [timePeriod, customStart, customEnd]);
  
  // Calculate comparison period (previous equivalent period)
  const comparisonRange = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return null;
    
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const durationMs = end.getTime() - start.getTime();
    const durationDays = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
    
    // Calculate previous period of same duration
    const prevEnd = new Date(start.getTime() - (1000 * 60 * 60 * 24)); // Day before current start
    const prevStart = new Date(prevEnd.getTime() - durationMs);
    
    return {
      start: formatDate(prevStart),
      end: formatDate(prevEnd),
    };
  }, [dateRange]);
  
  // Fetch analytics data for both periods
  useEffect(() => {
    const token = getToken();
    if (!token || !user || !dateRange.start || !dateRange.end) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const params = { start_date: dateRange.start, end_date: dateRange.end };
        
        // Fetch all analytics data in parallel (Phase 1, 2, and 3)
        const [
          summaryRes, jobsRes, productRes, decoRes, profitRes,
          quoteRes, labourRes, sellersRes, supplierRes, transferRes
        ] = await Promise.all([
          axios.get(`${API}/analytics/summary`, { params, headers }),
          axios.get(`${API}/analytics/jobs-over-time`, { params: { ...params, group_by: "week" }, headers }),
          axios.get(`${API}/analytics/product-demand`, { params, headers }),
          axios.get(`${API}/analytics/decoration`, { params, headers }),
          axios.get(`${API}/analytics/profitability`, { params, headers }),
          // Phase 3
          axios.get(`${API}/analytics/quote-conversion`, { params, headers }),
          axios.get(`${API}/analytics/labour-profitability`, { params, headers }),
          axios.get(`${API}/analytics/best-sellers`, { params, headers }),
          axios.get(`${API}/analytics/supplier`, { params, headers }),
          axios.get(`${API}/analytics/transfers`, { params, headers }),
        ]);
        
        setSummary(summaryRes.data);
        setJobsOverTime(jobsRes.data.data || []);
        setProductDemand(productRes.data);
        setDecoration(decoRes.data);
        setProfitability(profitRes.data);
        // Phase 3
        setQuoteConversion(quoteRes.data);
        setLabourProfitability(labourRes.data);
        setBestSellers(sellersRes.data);
        setSupplierAnalytics(supplierRes.data);
        setTransferAnalytics(transferRes.data);
        
        // Fetch comparison period data
        if (comparisonRange) {
          try {
            const prevRes = await axios.get(`${API}/analytics/summary`, {
              params: { start_date: comparisonRange.start, end_date: comparisonRange.end },
              headers,
            });
            setPrevSummary(prevRes.data);
          } catch (e) {
            setPrevSummary(null);
          }
        }
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user, dateRange.start, dateRange.end, comparisonRange]);
  
  // Calculate percentage change
  const calcChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };
  
  // Manual refresh function
  const handleRefresh = async () => {
    const token = getToken();
    if (!token || !dateRange.start || !dateRange.end) return;
    
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = { start_date: dateRange.start, end_date: dateRange.end };
      
      const [
        summaryRes, jobsRes, productRes, decoRes, profitRes,
        quoteRes, labourRes, sellersRes, supplierRes, transferRes
      ] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { params, headers }),
        axios.get(`${API}/analytics/jobs-over-time`, { params: { ...params, group_by: "week" }, headers }),
        axios.get(`${API}/analytics/product-demand`, { params, headers }),
        axios.get(`${API}/analytics/decoration`, { params, headers }),
        axios.get(`${API}/analytics/profitability`, { params, headers }),
        axios.get(`${API}/analytics/quote-conversion`, { params, headers }),
        axios.get(`${API}/analytics/labour-profitability`, { params, headers }),
        axios.get(`${API}/analytics/best-sellers`, { params, headers }),
        axios.get(`${API}/analytics/supplier`, { params, headers }),
        axios.get(`${API}/analytics/transfers`, { params, headers }),
      ]);
      
      setSummary(summaryRes.data);
      setJobsOverTime(jobsRes.data.data || []);
      setProductDemand(productRes.data);
      setDecoration(decoRes.data);
      setProfitability(profitRes.data);
      setQuoteConversion(quoteRes.data);
      setLabourProfitability(labourRes.data);
      setBestSellers(sellersRes.data);
      setSupplierAnalytics(supplierRes.data);
      setTransferAnalytics(transferRes.data);
      
      // Fetch comparison period data
      if (comparisonRange) {
        try {
          const prevRes = await axios.get(`${API}/analytics/summary`, {
            params: { start_date: comparisonRange.start, end_date: comparisonRange.end },
            headers,
          });
          setPrevSummary(prevRes.data);
        } catch (e) {
          setPrevSummary(null);
        }
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Prepare chart data
  const methodChartData = useMemo(() => {
    if (!summary?.by_method) return [];
    return Object.entries(summary.by_method).map(([name, value]) => ({
      name: name || "Unknown",
      value,
    }));
  }, [summary]);
  
  const clientTypeData = useMemo(() => {
    if (!summary?.by_client_type) return [];
    return [
      { name: "Retail", value: summary.by_client_type.retail?.jobs || 0, revenue: summary.by_client_type.retail?.revenue || 0 },
      { name: "Business", value: summary.by_client_type.business?.jobs || 0, revenue: summary.by_client_type.business?.revenue || 0 },
    ];
  }, [summary]);
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };
  
  return (
    <div className="min-h-screen bg-aa-bg">
      {/* Global Navigation */}
      <MainNav />

      {/* Page Controls Row */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-aa-container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-aa-md bg-aa-primary/10">
              <BarChart3 className="w-5 h-5 text-aa-primary" />
            </div>
            <div>
              <h1 className="text-aa-xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="text-aa-xs text-gray-500">Business insights & reporting</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-aa-md bg-aa-card shadow-aa-button text-aa-sm font-medium transition-all hover:-translate-y-0.5 active:shadow-aa-inset disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <main className="max-w-aa-container mx-auto px-6 py-6">
        {/* Time Period Selector */}
        <div className="bg-aa-card rounded-aa-lg p-6 shadow-aa-card mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-aa-xs text-gray-500 mb-2 block font-medium uppercase tracking-wide">Time Period</label>
              <Select value={timePeriod} onValueChange={setTimePeriod}>
                <SelectTrigger data-testid="time-period-select" className="bg-aa-bg shadow-aa-input border-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIME_PERIODS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {timePeriod === "custom" && (
              <>
                <div>
                  <label className="text-aa-xs text-gray-500 mb-2 block font-medium uppercase tracking-wide">Start Date</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="h-10 px-3 w-40 bg-aa-bg rounded-aa-md shadow-aa-input text-aa-sm focus:outline-none focus:ring-2 focus:ring-aa-primary"
                  />
                </div>
                <div>
                  <label className="text-aa-xs text-gray-500 mb-2 block font-medium uppercase tracking-wide">End Date</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="h-10 px-3 w-40 bg-aa-bg rounded-aa-md shadow-aa-input text-aa-sm focus:outline-none focus:ring-2 focus:ring-aa-primary"
                  />
                </div>
              </>
            )}
            
            <div className="flex items-center gap-2 text-aa-sm text-gray-600 bg-aa-bg px-4 py-2.5 rounded-aa-md shadow-aa-inset">
              <Calendar className="w-4 h-4" />
              <span>{dateRange.start} to {dateRange.end}</span>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-aa-primary" />
          </div>
        ) : !summary ? (
          <div className="bg-aa-card rounded-aa-lg shadow-aa-card py-12 text-center text-gray-500">
            Loading analytics data...
          </div>
        ) : (
          <div className="space-y-6">
            {/* Tab Navigation */}
            <div className="flex gap-2 flex-wrap">
              {[
                { id: "overview", label: "Overview", icon: BarChart3 },
                { id: "products", label: "Products", icon: Shirt },
                { id: "production", label: "Production", icon: Printer },
                { id: "financial", label: "Financial", icon: DollarSign },
                { id: "growth", label: "Growth", icon: TrendingUp },
              ].map(({ id, label, icon: TabIcon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-aa-md text-aa-sm font-medium transition-all ${
                    activeTab === id
                      ? "bg-aa-primary text-white shadow-md"
                      : "bg-aa-card shadow-aa-button hover:-translate-y-0.5 active:shadow-aa-inset"
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
            
            {/* OVERVIEW TAB */}
            {activeTab === "overview" && (
            <div>
              {/* Summary Metrics with Period Comparison */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <MetricCard
                  title="Total Revenue"
                  value={formatCurrency(summary.summary?.total_revenue)}
                  subtitle="ex GST"
                  icon={DollarSign}
                  color="primary"
                  description="Total sales value (excluding GST) from jobs marked Invoice Paid in this period. Unpaid quotes are excluded."
                  trend={calcChange(summary.summary?.total_revenue, prevSummary?.summary?.total_revenue)}
                />
                <MetricCard
                  title="Net Profit"
                  value={formatCurrency(summary.summary?.net_profit)}
                  subtitle="ex GST"
                  icon={TrendingUp}
                  color="green"
                  description="Total Revenue minus true production cost, for Invoice Paid jobs only."
                  trend={calcChange(summary.summary?.net_profit, prevSummary?.summary?.net_profit)}
                />
                <MetricCard
                  title="Jobs Completed"
                  value={summary.summary?.total_jobs || 0}
                  icon={Package}
                  color="amber"
                  description="Number of Invoice Paid jobs in this period."
                  trend={calcChange(summary.summary?.total_jobs, prevSummary?.summary?.total_jobs)}
                />
                <MetricCard
                  title="Garments Printed"
                  value={(summary.summary?.total_garments || 0).toLocaleString()}
                  icon={Shirt}
                  color="purple"
                  description="Total garment units across Invoice Paid jobs in this period."
                  trend={calcChange(summary.summary?.total_garments, prevSummary?.summary?.total_garments)}
                />
                <MetricCard
                title="Avg Order Value"
                value={formatCurrency(summary.summary?.avg_order_value)}
                icon={ShoppingBag}
                color="rose"
                description="Average revenue per Invoice Paid job (Total Revenue ÷ Jobs Completed)."
              />
              <MetricCard
                title="Avg Margin"
                value={`${summary.summary?.avg_margin_percent || 0}%`}
                icon={Percent}
                color="amber"
                description="Average profit margin across Invoice Paid jobs (Net Profit ÷ Total Revenue)."
              />
            </div>
            
            {/* Revenue Breakdown - Retail vs Business */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <MetricCard
                title="Retail Revenue"
                value={formatCurrency(summary.by_client_type?.retail?.revenue)}
                subtitle={`${summary.by_client_type?.retail?.jobs || 0} jobs • ex GST`}
                icon={ShoppingBag}
                color="orange"
                description="Revenue from Invoice Paid retail jobs (ex GST) in this period."
                trend={calcChange(summary.by_client_type?.retail?.revenue, prevSummary?.by_client_type?.retail?.revenue)}
              />
              <MetricCard
                title="Business Revenue"
                value={formatCurrency(summary.by_client_type?.business?.revenue)}
                subtitle={`${summary.by_client_type?.business?.jobs || 0} jobs • ex GST`}
                icon={Briefcase}
                color="primary"
                description="Revenue from Invoice Paid business jobs (ex GST) in this period."
                trend={calcChange(summary.by_client_type?.business?.revenue, prevSummary?.by_client_type?.business?.revenue)}
              />
              <Card className="shadow-sm">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Printer className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Prints</p>
                      <p className="font-semibold">{(summary.summary?.total_prints || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="shadow-sm">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Clock className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Revenue/Hour</p>
                      <p className="font-semibold">{formatCurrency(summary.summary?.revenue_per_hour)}</p>
                      <p className="text-xs text-muted-foreground">{summary.summary?.estimated_hours || 0} hrs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Jobs Over Time */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Jobs Over Time
                    <InfoTip testid="chart-info-jobs-over-time" text="Number of jobs quoted in each period (day/week) across the selected date range." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {jobsOverTime.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={jobsOverTime}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="period" 
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => {
                            const d = new Date(v);
                            return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
                          }}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip 
                          formatter={(value, name) => [value, name === 'jobs' ? 'Jobs' : name]}
                          labelFormatter={(v) => new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        />
                        <Bar dataKey="jobs" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No data for selected period
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Print Methods Distribution */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Printer className="w-4 h-4" />
                    Print Method Usage
                    <InfoTip testid="chart-info-print-method-usage-overview" text="Share of jobs using each decoration method in this period." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {methodChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={methodChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {methodChartData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No data for selected period
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Client Type Breakdown */}
            <Card className="shadow-sm mb-6">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Revenue by Client Type
                  <InfoTip testid="chart-info-revenue-by-client-type" text="Invoice Paid revenue (ex GST) split between Retail and Business clients." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {clientTypeData.length > 0 && (summary.by_client_type?.retail?.jobs > 0 || summary.by_client_type?.business?.jobs > 0) ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={clientTypeData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrency(v)} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="revenue" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                    No data for selected period
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
            )}
            
            {/* PRODUCTS TAB */}
            {activeTab === "products" && (
            <div>
            {/* Phase 2: Product Demand Analytics */}
            {productDemand && (
              <>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Shirt className="w-5 h-5" />
                  Product Demand Analytics
                  <InfoTip testid="chart-info-product-demand" text="What your customers order most — garment styles, colours and sizes across quotes in this period." />
                </h2>
                <div className="grid lg:grid-cols-3 gap-6 mb-6">
                  {/* Popular Styles */}
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Popular Garment Styles<InfoTip testid="chart-info-popular-styles" text="Most-ordered garment styles by total units in this period." /></CardTitle>
                    </CardHeader>
                    <CardContent>
                      {productDemand.popular_styles?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={productDemand.popular_styles.slice(0, 6)} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis type="number" tick={{ fontSize: 10 }} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                            <Tooltip />
                            <Bar dataKey="quantity" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
                          No style data
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Popular Colours */}
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Popular Colours<InfoTip testid="chart-info-popular-colours" text="Most-ordered garment colours by total units." /></CardTitle>
                    </CardHeader>
                    <CardContent>
                      {productDemand.popular_colours?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={productDemand.popular_colours.slice(0, 6)}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              paddingAngle={2}
                              dataKey="quantity"
                              nameKey="name"
                              label={({ name }) => name.substring(0, 8)}
                              labelLine={false}
                            >
                              {productDemand.popular_colours.slice(0, 6).map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
                          No colour data
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Popular Sizes */}
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Popular Sizes<InfoTip testid="chart-info-popular-sizes" text="Distribution of ordered garment sizes by total units." /></CardTitle>
                    </CardHeader>
                    <CardContent>
                      {productDemand.popular_sizes?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={productDemand.popular_sizes.slice(0, 8)}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Bar dataKey="quantity" fill="#10b981" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
                          No size data
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
            </div>
            )}
            
            {/* PRODUCTION TAB */}
            {activeTab === "production" && (
            <div>
            {/* Phase 2: Decoration Analytics */}
            {decoration && (
              <>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Printer className="w-5 h-5" />
                  Decoration Analytics
                  <InfoTip testid="chart-info-decoration-analytics" text="How your jobs are decorated — print locations, methods and print volumes in this period." />
                </h2>
                <div className="grid lg:grid-cols-3 gap-6 mb-6">
                  {/* Popular Print Locations */}
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Popular Print Locations<InfoTip testid="chart-info-print-locations" text="Most-used print placements (e.g. Left Chest, Full Back) across jobs." /></CardTitle>
                    </CardHeader>
                    <CardContent>
                      {decoration.popular_locations?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={decoration.popular_locations.slice(0, 6)} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis type="number" tick={{ fontSize: 10 }} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
                          No location data
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Method Usage */}
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Print Method Usage<InfoTip testid="chart-info-method-usage-production" text="Number of jobs and total prints for each decoration method." /></CardTitle>
                    </CardHeader>
                    <CardContent>
                      {decoration.method_usage?.length > 0 ? (
                        <div className="space-y-3 pt-2">
                          {decoration.method_usage.slice(0, 5).map((method, i) => (
                            <div key={`method-${method.name}-${i}`} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                <span className="text-sm font-medium">{method.name}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-semibold">{method.jobs} jobs</span>
                                <span className="text-xs text-muted-foreground ml-2">({method.prints} prints)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
                          No method data
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Decoration Summary */}
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Decoration Summary<InfoTip testid="chart-info-decoration-summary" text="Total prints plus average prints per garment and per job." /></CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4 pt-2">
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Total Prints</span>
                          <span className="text-lg font-bold">{(decoration.summary?.total_prints || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Avg Prints/Garment</span>
                          <span className="text-lg font-bold">{decoration.summary?.avg_prints_per_garment || 0}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Avg Prints/Job</span>
                          <span className="text-lg font-bold">{decoration.summary?.avg_prints_per_job || 0}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
            
            {/* Transfer Analytics */}
            {transferAnalytics && (
              <>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Layers className="w-5 h-5" />
                  Transfer Usage Analytics
                  <InfoTip testid="chart-info-transfer-analytics" text="Transfer / print-sheet usage and cost across jobs using transfer-based methods." />
                </h2>
                <div className="grid lg:grid-cols-3 gap-6 mb-6">
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Transfer Summary<InfoTip testid="chart-info-transfer-summary" text="Total transfers, estimated sheets used and average transfers per job." /></CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Total Transfers</span>
                          <span className="text-lg font-bold text-purple-700">{(transferAnalytics.summary?.total_transfers || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Sheets Used (est.)</span>
                          <span className="text-lg font-bold">{transferAnalytics.summary?.estimated_sheets_used || 0}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Avg/Job</span>
                          <span className="text-lg font-bold">{transferAnalytics.summary?.avg_transfers_per_job || 0}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">By Method<InfoTip testid="chart-info-transfer-by-method" text="Jobs using each transfer-based method (Hybrid/UltraColour, DTF, Screen Print)." /></CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex justify-between items-center p-2 bg-amber-50 rounded">
                          <span className="text-sm">Hybrid/UltraColour</span>
                          <Badge className="bg-amber-600">{transferAnalytics.by_method?.hybrid_ultracolour || 0} jobs</Badge>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                          <span className="text-sm">DTF</span>
                          <Badge className="bg-blue-600">{transferAnalytics.by_method?.dtf || 0} jobs</Badge>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                          <span className="text-sm">Screen Print</span>
                          <Badge className="bg-green-600">{transferAnalytics.by_method?.screen_print || 0} jobs</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Cost Analysis<InfoTip testid="chart-info-transfer-cost" text="Total transfer cost, cost per garment and prints per sheet." /></CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 pt-2">
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Total Cost</span>
                          <span className="text-lg font-bold">{formatCurrency(transferAnalytics.summary?.total_transfer_cost)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Cost/Garment</span>
                          <span className="text-lg font-bold">{formatCurrency(transferAnalytics.summary?.cost_per_garment)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Prints/Sheet</span>
                          <span className="text-lg font-bold text-blue-700">{transferAnalytics.efficiency?.prints_per_sheet || 5}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
            </div>
            )}
            
            {/* FINANCIAL TAB */}
            {activeTab === "financial" && (
            <div>
              {/* Quote Conversion Module */}
              {quoteConversion && (
                <>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Quote Conversion
                    <InfoTip testid="chart-info-quote-conversion" text="How many quotes were sent versus approved, and the revenue they represent." />
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <Card className="shadow-sm">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground font-medium">Quotes Sent</p>
                            <p className="text-2xl font-bold mt-1">{quoteConversion.quotes_sent}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
                            <FileText className="w-5 h-5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground font-medium">Quotes Approved</p>
                            <p className="text-2xl font-bold mt-1">{quoteConversion.quotes_approved}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-green-50 text-green-600">
                            <Target className="w-5 h-5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm border-2 border-primary">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground font-medium">Conversion Rate</p>
                            <p className="text-2xl font-bold mt-1 text-primary">{quoteConversion.conversion_rate}%</p>
                          </div>
                          <div className="p-3 rounded-lg bg-primary/10 text-primary">
                            <Percent className="w-5 h-5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground font-medium">Lost Revenue</p>
                            <p className="text-2xl font-bold mt-1 text-red-600">{formatCurrency(quoteConversion.revenue_lost)}</p>
                            <p className="text-xs text-muted-foreground mt-1">from declined/cancelled</p>
                          </div>
                          <div className="p-3 rounded-lg bg-red-50 text-red-600">
                            <TrendingDown className="w-5 h-5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <Card className="shadow-sm">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Revenue Approved</span>
                          <span className="text-lg font-bold text-green-700">{formatCurrency(quoteConversion.revenue_approved)}</span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Avg Quote Value</span>
                          <span className="text-lg font-bold">{formatCurrency(quoteConversion.avg_quote_value)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
              
              {/* Labour Profitability Module */}
              {labourProfitability && (
                <>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Labour Profitability
                    <InfoTip testid="chart-info-labour-profitability" text="Revenue and profit generated per production hour." />
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <Card className="shadow-sm">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground font-medium">Total Hours</p>
                            <p className="text-2xl font-bold mt-1">{labourProfitability.summary?.total_hours || 0}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-purple-50 text-purple-600">
                            <Clock className="w-5 h-5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground font-medium">Revenue/Hour</p>
                            <p className="text-2xl font-bold mt-1">{formatCurrency(labourProfitability.summary?.revenue_per_hour)}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
                            <DollarSign className="w-5 h-5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm border-2 border-green-500">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground font-medium">Profit/Hour</p>
                            <p className="text-2xl font-bold mt-1 text-green-600">{formatCurrency(labourProfitability.summary?.profit_per_hour)}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-green-50 text-green-600">
                            <TrendingUp className="w-5 h-5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground font-medium">Total Profit</p>
                            <p className="text-2xl font-bold mt-1">{formatCurrency(labourProfitability.summary?.total_profit)}</p>
                          </div>
                          <div className="p-3 rounded-lg bg-amber-50 text-amber-600">
                            <DollarSign className="w-5 h-5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="grid lg:grid-cols-2 gap-6 mb-6">
                    {/* By Client Type */}
                    <Card className="shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Profitability by Client Type<InfoTip testid="chart-info-profit-client-type" text="Revenue and profit generated per hour for each client type." /></CardTitle>
                      </CardHeader>
                      <CardContent>
                        {labourProfitability.by_client_type?.length > 0 ? (
                          <div className="space-y-3 pt-2">
                            {labourProfitability.by_client_type.map((ct, i) => (
                              <div key={`client-type-${ct.name}-${i}`} className="p-3 bg-slate-50 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="font-medium">{ct.name}</span>
                                  <Badge variant="outline">{ct.jobs} jobs • {ct.hours}h</Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground text-xs">Revenue/Hour</p>
                                    <p className="font-semibold">{formatCurrency(ct.revenue_per_hour)}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-xs">Profit/Hour</p>
                                    <p className="font-semibold text-green-600">{formatCurrency(ct.profit_per_hour)}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="h-[150px] flex items-center justify-center text-muted-foreground text-sm">
                            No client type data
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* By Print Method */}
                    <Card className="shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Profitability by Print Method<InfoTip testid="chart-info-profit-method" text="Profit generated per hour for each print method." /></CardTitle>
                      </CardHeader>
                      <CardContent>
                        {labourProfitability.by_method?.length > 0 ? (
                          <div className="space-y-2 pt-2">
                            {labourProfitability.by_method.slice(0, 6).map((method, i) => (
                              <div key={`labour-method-${method.name}-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                  <span className="text-sm font-medium">{method.name}</span>
                                  <span className="text-xs text-muted-foreground">({method.jobs} jobs)</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-semibold text-green-600">{formatCurrency(method.profit_per_hour)}/hr</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="h-[150px] flex items-center justify-center text-muted-foreground text-sm">
                            No method data
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
              
              {/* Phase 2: Profitability Analytics */}
              {profitability && (
                <>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Margin Analysis
                    <InfoTip testid="chart-info-margin-analysis" text="Profit margins broken down by client type and print method." />
                  </h2>
                  <div className="grid lg:grid-cols-2 gap-6 mb-6">
                    {/* Margin by Client Type */}
                    <Card className="shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Margin by Client Type<InfoTip testid="chart-info-margin-client-type" text="Revenue, cost and profit margin for each client type." /></CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4 pt-2">
                          {Object.entries(profitability.by_client_type || {}).map(([type, data]) => (
                            <div key={type} className="p-3 bg-slate-50 rounded-lg">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-medium">{type}</span>
                                <Badge variant={data.margin_percent >= 30 ? "default" : "secondary"}>
                                  {data.margin_percent}% margin
                                </Badge>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div>
                                  <p className="text-muted-foreground">Revenue</p>
                                  <p className="font-semibold">{formatCurrency(data.revenue)}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Cost</p>
                                  <p className="font-semibold text-red-600">{formatCurrency(data.cost)}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Profit</p>
                                  <p className="font-semibold text-green-600">{formatCurrency(data.profit)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Margin by Print Method */}
                    <Card className="shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Margin by Print Method<InfoTip testid="chart-info-margin-method" text="Profit margin achieved for each print method." /></CardTitle>
                      </CardHeader>
                      <CardContent>
                        {profitability.by_method?.length > 0 ? (
                          <div className="space-y-3 pt-2">
                            {profitability.by_method.slice(0, 5).map((method, i) => (
                              <div key={`profit-method-${method.name}-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <div>
                                  <span className="text-sm font-medium">{method.name}</span>
                                  <span className="text-xs text-muted-foreground ml-2">({method.jobs} jobs)</span>
                                </div>
                                <div className="text-right">
                                  <Badge variant={method.margin_percent >= 30 ? "default" : "secondary"} className="text-xs">
                                    {method.margin_percent}%
                                  </Badge>
                                  <p className="text-xs text-green-600 mt-1">{formatCurrency(method.profit)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
                            No method profitability data
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
              
              {/* Supplier Analytics Module */}
              {supplierAnalytics && (
                <>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5" />
                    Supplier Analytics
                    <InfoTip testid="chart-info-supplier-analytics" text="Purchasing activity and shipping thresholds by supplier." />
                  </h2>
                  <div className="grid lg:grid-cols-2 gap-6 mb-6">
                    {/* AS Colour Summary */}
                    <Card className="shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">AS Colour Purchases<InfoTip testid="chart-info-ascolour" text="Total AS Colour garment spend and units in this period." /></CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 pt-2">
                          <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                            <span className="text-sm text-muted-foreground">Total Purchases</span>
                            <span className="text-lg font-bold text-blue-700">{formatCurrency(supplierAnalytics.as_colour?.total_purchases)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm text-muted-foreground">Total Orders</span>
                            <span className="text-lg font-bold">{supplierAnalytics.as_colour?.total_orders || 0}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm text-muted-foreground">Avg Order Value</span>
                            <span className="text-lg font-bold">{formatCurrency(supplierAnalytics.as_colour?.avg_order_value)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Shipping Threshold Analysis */}
                    <Card className="shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Shipping Threshold Analysis<InfoTip testid="chart-info-shipping-threshold" text="How often orders reach free-shipping thresholds versus incur shipping." /></CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 pt-2">
                          <div className="text-xs text-muted-foreground mb-2">
                            Free shipping threshold: {formatCurrency(supplierAnalytics.as_colour?.shipping_threshold)}
                          </div>
                          <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                            <span className="text-sm">Orders above threshold</span>
                            <Badge className="bg-green-600">{supplierAnalytics.as_colour?.orders_above_threshold || 0}</Badge>
                          </div>
                          <div className="flex justify-between items-center p-2 bg-amber-50 rounded">
                            <span className="text-sm">Orders below threshold</span>
                            <Badge className="bg-amber-600">{supplierAnalytics.as_colour?.orders_below_threshold || 0}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-3">
                            <div className="p-2 bg-green-50 rounded text-center">
                              <p className="text-xs text-muted-foreground">Shipping Saved</p>
                              <p className="font-semibold text-green-600">{formatCurrency(supplierAnalytics.as_colour?.estimated_shipping_saved)}</p>
                            </div>
                            <div className="p-2 bg-red-50 rounded text-center">
                              <p className="text-xs text-muted-foreground">Shipping Lost</p>
                              <p className="font-semibold text-red-600">{formatCurrency(supplierAnalytics.as_colour?.estimated_shipping_lost)}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* All Suppliers */}
                  {supplierAnalytics.all_suppliers?.length > 0 && (
                    <Card className="shadow-sm mb-6">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">All Suppliers<InfoTip testid="chart-info-all-suppliers" text="Spend and order counts across every supplier." /></CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 pt-2">
                          {supplierAnalytics.all_suppliers.map((supplier, i) => (
                            <div key={`supplier-${supplier.name}-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                <span className="text-sm font-medium">{supplier.name}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-semibold">{formatCurrency(supplier.cost)}</span>
                                <span className="text-xs text-muted-foreground ml-2">({supplier.orders} orders)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
            )}
            
            {/* GROWTH TAB */}
            {activeTab === "growth" && (
            <div>
              {/* Best Sellers Module */}
              {bestSellers && (
                <>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Best Selling Combinations
                    <InfoTip testid="chart-info-best-sellers" text="Your top-selling garment + decoration combinations by units sold." />
                  </h2>
                  <Card className="shadow-sm mb-6">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Top Products by Units Sold<InfoTip testid="chart-info-top-products" text="Best-selling garment + decoration combinations ranked by units." /></CardTitle>
                        <Badge variant="outline">{bestSellers.total_combinations} unique combinations</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {bestSellers.best_sellers?.length > 0 ? (
                        <div className="space-y-3 pt-2">
                          {bestSellers.best_sellers.slice(0, 10).map((item, i) => (
                            <div key={`bestseller-${item.product_name || item.style_code}-${i}`} className="p-3 bg-slate-50 rounded-lg">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                                    {i + 1}
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{item.garment}</p>
                                    <p className="text-xs text-muted-foreground">{item.colour}</p>
                                  </div>
                                </div>
                                <Badge variant="secondary" className="text-xs">
                                  {item.units} units
                                </Badge>
                              </div>
                              <div className="ml-8 text-xs">
                                <p className="text-muted-foreground mb-1">{item.design}</p>
                                <div className="flex gap-4">
                                  <span className="text-green-600 font-medium">{formatCurrency(item.revenue)} revenue</span>
                                  <span className="text-muted-foreground">{item.jobs} jobs</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
                          No sales data for selected period
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Best Sellers Chart */}
                  {bestSellers.best_sellers?.length > 0 && (
                    <Card className="shadow-sm mb-6">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">Units Sold - Top 8<InfoTip testid="chart-info-units-top8" text="The eight highest-selling product combinations by units." /></CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart 
                            data={bestSellers.best_sellers.slice(0, 8).map(item => ({
                              name: `${item.garment} (${item.colour})`.substring(0, 20),
                              units: item.units,
                              revenue: item.revenue,
                            }))}
                            layout="vertical"
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis type="number" tick={{ fontSize: 10 }} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                            <Tooltip 
                              formatter={(value, name) => [
                                name === 'units' ? value : formatCurrency(value),
                                name === 'units' ? 'Units Sold' : 'Revenue'
                              ]}
                            />
                            <Bar dataKey="units" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
              
              {/* Growth Insights */}
              <Card className="shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Growth Insights
                    <InfoTip testid="chart-info-growth-insights" text="Period-over-period trends highlighting what's growing or slowing." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4 pt-2">
                    {quoteConversion && (
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-800 mb-1">Quote Conversion</p>
                        <p className="text-xs text-blue-600">
                          {quoteConversion.conversion_rate >= 60 
                            ? "Strong conversion rate! Focus on increasing quote volume."
                            : quoteConversion.conversion_rate >= 40
                            ? "Average conversion. Review lost quotes for improvement opportunities."
                            : "Low conversion rate. Consider follow-up processes and pricing review."
                          }
                        </p>
                      </div>
                    )}
                    {labourProfitability && (
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-sm font-medium text-green-800 mb-1">Labour Efficiency</p>
                        <p className="text-xs text-green-600">
                          {labourProfitability.summary?.profit_per_hour >= 100
                            ? "Excellent profit per hour! Consider capacity expansion."
                            : labourProfitability.summary?.profit_per_hour >= 50
                            ? "Good hourly profitability. Focus on high-margin methods."
                            : "Review pricing or process efficiency to improve margins."
                          }
                        </p>
                      </div>
                    )}
                    {bestSellers && bestSellers.best_sellers?.length > 0 && (
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm font-medium text-purple-800 mb-1">Top Performer</p>
                        <p className="text-xs text-purple-600">
                          "{bestSellers.best_sellers[0].garment}" in {bestSellers.best_sellers[0].colour} is your best seller 
                          with {bestSellers.best_sellers[0].units} units. Consider stocking more.
                        </p>
                      </div>
                    )}
                    {supplierAnalytics && (
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <p className="text-sm font-medium text-amber-800 mb-1">Supplier Opportunity</p>
                        <p className="text-xs text-amber-600">
                          {supplierAnalytics.as_colour?.orders_below_threshold > 0
                            ? `${supplierAnalytics.as_colour.orders_below_threshold} orders missed free shipping. Batch orders when possible.`
                            : "Great job batching orders to hit free shipping thresholds!"
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
